import { createSelector } from '@ngrx/store';
import { CartItemState } from '../reducers/cart-item.reducer';
import { CartItemModuleState, getCartItemModuleState } from '../reducers/index';
import { CartItem, CartItemTotal } from '../types/cart-item.type';
import { DeliveryMethod } from '../types/delivery-method.type';

const getCartItemState = createSelector(
  getCartItemModuleState,
  (state: CartItemModuleState) => state.cartItem,
);

const getIndexError = (state: CartItemState) => state.index.error;

export const getCartItems = createSelector(
  getCartItemState,
  (state: CartItemState) => state.cartItems,
);

export const getCartItemIndexError = createSelector(
  getCartItemState,
  getIndexError,
);

export const getCartItemAddLoading = createSelector(
  getCartItemState,
  (state: CartItemState) => state.post.loading,
);

export const getCartItemIndexLoading = createSelector(
  getCartItemState,
  (state: CartItemState) => state.index.loading && !state.index.hasLoaded,
);

export const getCartItemIndexRefreshing = createSelector(
  getCartItemState,
  (state: CartItemState) => state.index.loading && state.index.hasLoaded,
);

export const getCartItemsArray = createSelector(
  getCartItems,
  (cartItems: { [id: string]: Partial<CartItem> }): CartItem[] =>
    Object.values(cartItems) as CartItem[],
);

export const getAvailableCartItemsArray = createSelector(
  getCartItemsArray,
  (cartItems: CartItem[]): CartItem[] =>
    cartItems.filter((cartItem) => cartItem.available),
);

export const getUnAvailableCartItemsArray = createSelector(
  getCartItemsArray,
  (cartItems: CartItem[]): CartItem[] =>
    cartItems.filter((cartItem) => !cartItem.available),
);

// Selectors based on available cart items only. We do not need the total line items of
// unavailable cart items yet.
export const getCartItemsTotalLineItems = createSelector(
  getAvailableCartItemsArray,
  (cartItems: CartItem[]): number => {
    const reducer = (previousValue: number, currentValue: CartItem): number => {
      return previousValue + currentValue.quantity;
    };

    return cartItems.reduce(reducer, 0);
  },
);

export const getCartItemsTotal = createSelector(
  getAvailableCartItemsArray,
  (cartItems: CartItem[]): CartItemTotal => {
    const reducer = (
      previousValue: CartItemTotal,
      currentValue: CartItem,
    ): CartItemTotal => {
      return {
        pointsAmount: previousValue.pointsAmount + currentValue.pointsAmount,
        cashAmount: previousValue.cashAmount + currentValue.cashAmount,
      };
    };

    return cartItems.reduce(reducer, { pointsAmount: 0, cashAmount: 0 });
  },
);

export const getCartItemsDeliveryMethods = createSelector(
  getAvailableCartItemsArray,
  (cartItems: CartItem[]): DeliveryMethod[] => {
    const allDeliveryMethods = Array.from(
      new Set(
        cartItems
          .map((cartItem) => cartItem.deliveryMethods)
          .reduce((previous, current) => {
            return previous.concat(current);
          }, []),
      ),
    );

    return allDeliveryMethods;
  },
);

export const getCartItemsShouldHaveAddressType = createSelector(
  getCartItemsDeliveryMethods,
  (deliveryMethods: DeliveryMethod[]): boolean => {
    return deliveryMethods.includes('address');
  },
);

// New cart api related selectors

export const getCartPromocode = createSelector(
  getCartItemState,
  (state: CartItemState) => state.promocode.value,
);

export const getCartPromocodeUsage = createSelector(
  getCartItemState,
  (state: CartItemState) => state.promocode.usage,
);

export const getCartPromocodeError = createSelector(
  getCartItemState,
  (state: CartItemState) => state.promocode.post.error,
);

export const getCartPromocodeLoadingState = createSelector(
  getCartItemState,
  (state: CartItemState) => state.promocode.post.loading,
);
